import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  ChatCitation,
  ChatMessageHistoryItem,
  ChatQueryResponse,
  ChatService,
  ChatSessionSummary
} from '../../../services/chat.service';
import { SSOService, SSOUser } from '../../../services/sso.service';

interface ChatUiMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  chatMessageId?: string;
  question?: string;
  citations?: ChatCitation[];
  feedback?: 'positive' | 'negative';
}

@Component({
  selector: 'app-chat-bubble',
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-bubble.html',
  styleUrl: './chat-bubble.css'
})
export class ChatBubbleComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly activeSessionStorageKey = 'kansei_chat_active_session_id';
  private currentUser: SSOUser | null = null;

  isOpen = signal(false);
  loading = signal(false);
  loadingHistory = signal(false);
  canShowChat = signal(false);
  productLines = signal<string[]>([]);
  selectedProductLine = signal('NINJA');
  sessions = signal<ChatSessionSummary[]>([]);
  messages = signal<ChatUiMessage[]>([]);
  errorMessage = signal('');
  feedbackMessage = signal('');
  activeSessionId = signal('');

  draftMessage = '';

  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messagesViewport') messagesViewport?: ElementRef<HTMLDivElement>;

  constructor(
    private chatService: ChatService,
    private ssoService: SSOService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.activeSessionId.set(this.getStoredSessionId() || this.createSessionId());
    this.storeActiveSessionId(this.activeSessionId());

    this.ssoService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.updateChatVisibility(user);
        if (user?.email) {
          void this.loadSessions();
        }
      });

    this.currentUser = this.ssoService.getCurrentUser();
    this.updateChatVisibility(this.currentUser);
    void this.loadProductLines();
    if (this.currentUser?.email) {
      void this.loadSessions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleOpen(): void {
    this.isOpen.update(value => !value);
    this.errorMessage.set('');

    if (this.isOpen()) {
      setTimeout(() => this.messageInput?.nativeElement.focus(), 0);
      this.scrollMessagesToBottom();
    }
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  setProductLine(productLine: string): void {
    if (!productLine || this.loading()) {
      return;
    }

    this.selectedProductLine.set(productLine);
  }

  async startNewChat(): Promise<void> {
    const sessionId = this.createSessionId();
    this.activeSessionId.set(sessionId);
    this.storeActiveSessionId(sessionId);
    this.messages.set([]);
    this.errorMessage.set('');
    this.feedbackMessage.set('');
    setTimeout(() => this.messageInput?.nativeElement.focus(), 0);
  }

  async loadSession(sessionId: string): Promise<void> {
    const userId = this.getUserId();
    if (!sessionId || !userId) {
      return;
    }

    this.loadingHistory.set(true);
    this.errorMessage.set('');

    try {
      const response = await firstValueFrom(this.chatService.getSessionMessages(sessionId, userId));
      this.activeSessionId.set(sessionId);
      this.storeActiveSessionId(sessionId);
      this.messages.set((response.value || []).map(item => this.mapHistoryMessage(item)));
      this.scrollMessagesToBottom();
    } catch {
      this.errorMessage.set('Unable to load this chat session.');
    } finally {
      this.loadingHistory.set(false);
    }
  }

  async sendMessage(): Promise<void> {
    const query = this.draftMessage.trim();
    const userId = this.getUserId();
    const productLine = this.selectedProductLine();

    if (!query || this.loading()) {
      return;
    }

    if (!userId) {
      this.errorMessage.set('Unable to identify the signed-in user for chat.');
      return;
    }

    if (!productLine) {
      this.errorMessage.set('Select a product line before sending a message.');
      return;
    }

    const userMessage: ChatUiMessage = {
      id: this.createId(),
      role: 'user',
      text: query,
      timestamp: new Date()
    };

    this.messages.update(messages => [...messages, userMessage]);
    this.draftMessage = '';
    this.loading.set(true);
    this.errorMessage.set('');
    this.feedbackMessage.set('');
    this.scrollMessagesToBottom();

    try {
      const response = await firstValueFrom(this.chatService.query({
        query,
        product_line: productLine,
        user_id: userId,
        include_images: false,
        session_id: this.activeSessionId()
      }));

      this.messages.update(messages => [...messages, this.mapQueryResponse(response, query)]);
      await this.loadSessions();
      this.scrollMessagesToBottom();
    } catch {
      this.errorMessage.set('Chat service is unavailable. Please try again.');
      this.draftMessage = query;
    } finally {
      this.loading.set(false);
    }
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.sendMessage();
    }
  }

  async submitFeedback(message: ChatUiMessage, feedbackType: 'positive' | 'negative'): Promise<void> {
    const userId = this.getUserId();
    if (!userId || !message.chatMessageId || !message.question) {
      return;
    }

    try {
      await firstValueFrom(this.chatService.submitFeedback({
        chat_message_id: message.chatMessageId,
        user_id: userId,
        session_id: this.activeSessionId(),
        feedback_type: feedbackType,
        question: message.question,
        answer: message.text,
        correction_text: null,
        reason: null
      }));

      this.messages.update(messages => messages.map(item =>
        item.id === message.id ? { ...item, feedback: feedbackType } : item
      ));
      this.feedbackMessage.set('Feedback submitted.');
      setTimeout(() => this.feedbackMessage.set(''), 2500);
    } catch {
      this.errorMessage.set('Unable to submit feedback right now.');
    }
  }

  async openCitation(citation: ChatCitation): Promise<void> {
    const source = citation.document || '';
    if (!source || !isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const directUrl = this.getCitationUrl(citation);
      if (directUrl && !this.isPlaceholderCitationUrl(directUrl)) {
        this.navigateCitationWindow(directUrl);
        return;
      }

      const response = await firstValueFrom(this.chatService.getCitationDownload(source));
      const downloadUrl = this.getCitationUrl(response);

      if (!downloadUrl || this.isPlaceholderCitationUrl(downloadUrl)) {
        this.errorMessage.set('Citation file download is not configured by the chat API yet.');
        return;
      }

      this.navigateCitationWindow(downloadUrl);
    } catch {
      this.errorMessage.set('Unable to open citation file.');
    }
  }

  trackMessage(_index: number, message: ChatUiMessage): string {
    return message.id;
  }

  trackSession(_index: number, session: ChatSessionSummary): string {
    return session.session_id;
  }

  trackProductLine(_index: number, productLine: string): string {
    return productLine;
  }

  trackCitation(index: number, citation: ChatCitation): string {
    return citation.chunk_id || `${citation.document || 'citation'}-${citation.reference_number || index}`;
  }

  private async loadProductLines(): Promise<void> {
    try {
      const response = await firstValueFrom(this.chatService.getProductLines());
      const productLines = response.product_lines?.filter(Boolean) || [];
      this.productLines.set(productLines);
      this.selectedProductLine.set(productLines.includes('NINJA') ? 'NINJA' : productLines[0] || 'NINJA');
    } catch {
      this.productLines.set(['NINJA', 'TM10E2']);
      this.selectedProductLine.set('NINJA');
    }
  }

  private async loadSessions(): Promise<void> {
    const userId = this.getUserId();
    if (!userId) {
      return;
    }

    try {
      const response = await firstValueFrom(this.chatService.getSessions(userId));
      this.sessions.set(response.value || []);
    } catch {
      this.sessions.set([]);
    }
  }

  private mapQueryResponse(response: ChatQueryResponse, question: string): ChatUiMessage {
    return {
      id: this.createId(),
      role: 'assistant',
      text: response.answer || 'No answer was returned.',
      timestamp: new Date(),
      chatMessageId: response.chat_message_id,
      question,
      citations: response.citations || []
    };
  }

  private mapHistoryMessage(item: ChatMessageHistoryItem): ChatUiMessage {
    return {
      id: item.id || this.createId(),
      role: item.role === 'assistant' ? 'assistant' : 'user',
      text: item.message,
      timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
      chatMessageId: item.role === 'assistant' ? item.msg_id || undefined : undefined
    };
  }

  private getUserId(): string {
    return (this.currentUser?.email || this.ssoService.getCurrentUser()?.email || '').trim();
  }

  private updateChatVisibility(user: SSOUser | null): void {
    const isTmeicUser = (user?.userTypeName || '').trim().toUpperCase() === 'TMEIC';
    this.canShowChat.set(isTmeicUser);

    if (!isTmeicUser) {
      this.isOpen.set(false);
    }
  }

  private getStoredSessionId(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return '';
    }

    return localStorage.getItem(this.activeSessionStorageKey) || '';
  }

  private storeActiveSessionId(sessionId: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.activeSessionStorageKey, sessionId);
    }
  }

  private createSessionId(): string {
    return `kansei-ui-${this.createId()}`;
  }

  private createId(): string {
    if (isPlatformBrowser(this.platformId) && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private isPlaceholderCitationUrl(url: string): boolean {
    try {
      return new URL(url).hostname.toLowerCase() === 'example.com';
    } catch {
      return false;
    }
  }

  private getCitationUrl(value: Partial<ChatCitation & { source?: string }>): string {
    const candidate = value.download_url || value.downloadUrl || value.url || value.file_url || '';
    if (candidate) {
      return candidate;
    }

    const source = value.source || value.document || '';
    if (!source) {
      return '';
    }

    try {
      const url = new URL(source);
      return url.protocol === 'http:' || url.protocol === 'https:' ? source : '';
    } catch {
      return '';
    }
  }

  private navigateCitationWindow(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private scrollMessagesToBottom(): void {
    setTimeout(() => {
      const viewport = this.messagesViewport?.nativeElement;
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }, 0);
  }
}
